import React, { useEffect, useState } from 'react';
import {
  FilePlus,
  Pencil,
  RefreshCw,
  Percent,
  DollarSign,
  Undo2,
  GitBranch,
  Trash2,
  Eye,
  CheckCircle2,
  XCircle,
  Send,
  Link2Off,
  ClipboardList,
} from 'lucide-react';
import { authFetch } from '../utils/authFetch';
import API_BASE from '../../utils/api.js';
import './ActivityFeed.css';

const ACTION_ICONS = {
  created: FilePlus,
  updated: Pencil,
  line_items_changed: ClipboardList,
  status_changed: RefreshCw,
  processing_fee_changed: Percent,
  payment_added: DollarSign,
  payment_edited: DollarSign,
  payment_removed: Undo2,
  payment_refunded: Undo2,
  payments_revoked: Undo2,
  share_revoked: Link2Off,
  approval_revoked: Link2Off,
  converted_to_po: GitBranch,
  converted_to_invoice: GitBranch,
  deleted: Trash2,
  client_viewed: Eye,
  client_selection_changed: Pencil,
  client_item_approved: CheckCircle2,
  client_item_declined: XCircle,
  client_approved: CheckCircle2,
  client_changes_requested: Send,
  client_payment_made: DollarSign,
};

function formatRelativeTime(isoString) {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.round(diffMs / 1000);

  if (diffSec < 60) return 'Just now';
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;

  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function ActivityFeed({ userId, projectId, entityType, entityId }) {
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId || !projectId || !entityId) {
      setLoading(false);
      return;
    }

    let mounted = true;
    const routeSegment = entityType === 'invoice' ? 'invoices' : 'proposals';

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await authFetch(
          `${API_BASE}/api/${routeSegment}/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}/${encodeURIComponent(entityId)}/activity`,
          { cache: 'no-store' }
        );
        if (!res.ok) throw new Error('Failed to load activity');
        const data = await res.json();
        if (mounted) setActivity(Array.isArray(data.activity) ? data.activity : []);
      } catch (err) {
        if (mounted) setError(err.message || 'Failed to load activity.');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [userId, projectId, entityType, entityId]);

  return (
    <div className="activity-feed">
      <h4 className="activity-feed-title">Activity</h4>

      {loading && <div className="activity-feed-empty">Loading activity...</div>}
      {!loading && error && <div className="activity-feed-empty">{error}</div>}
      {!loading && !error && activity.length === 0 && (
        <div className="activity-feed-empty">No activity yet.</div>
      )}

      {!loading && !error && activity.length > 0 && (
        <ul className="activity-feed-list">
          {activity.map((entry) => {
            const Icon = ACTION_ICONS[entry.action] || Pencil;
            const isClient = entry.actorType === 'client';
            return (
              <li key={`${entry.entityId}-${entry.timestamp}`} className="activity-feed-row">
                <span className={`activity-feed-icon${isClient ? ' activity-feed-icon-client' : ''}`}>
                  <Icon size={15} />
                </span>
                <div className="activity-feed-text">
                  <div className="activity-feed-summary">{entry.summary}</div>
                  {Array.isArray(entry.metadata?.changes) && entry.metadata.changes.length > 0 && (
                    <ul className="activity-feed-changes">
                      {entry.metadata.changes.map((change, idx) => (
                        <li key={`${change.itemId || idx}`} className="activity-feed-change-row">
                          <span className={`activity-feed-change-badge activity-feed-change-${change.type}`}>
                            {change.type === 'added' ? 'Added' : change.type === 'removed' ? 'Removed' : 'Updated'}
                          </span>
                          <span className="activity-feed-change-item">{change.itemName}</span>
                          {Array.isArray(change.fields) && change.fields.length > 0 && (
                            <ul className="activity-feed-field-list">
                              {change.fields.map((f) => (
                                <li key={f.field}>
                                  {f.label}: {f.oldValue ?? '—'} → {f.newValue ?? '—'}
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="activity-feed-meta" title={new Date(entry.createdAt).toLocaleString()}>
                    {entry.actorName && <span className="activity-feed-actor">{entry.actorName}</span>}
                    {entry.actorName ? ' · ' : ''}
                    {formatRelativeTime(entry.createdAt)}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
